<template>
  <div
    class="grid gap-5 text-left md:grid-cols-[minmax(0,1fr)_340px] md:items-start"
    data-testid="send-record-step-5"
  >
    <!--
      Celular: o anexo sobe para o topo, como na etapa 4. `v-if` e não `md:hidden`
      para não montar os dois painéis (e carregar cada anexo duas vezes).
    -->
    <div v-if="files.length && !isDesktop">
      <AttachmentPanel
        layout="carousel"
        :files="files"
        :active-index="activeIndex"
        @update:active-index="emit('update:activeIndex', $event)"
      />
    </div>

    <div class="flex flex-col gap-y-3">
      <dl class="rounded-lg border border-lightGray30 divide-y divide-gray-100">
        <div class="flex gap-3 px-3 py-2">
          <dt class="w-28 text-xs text-neutral50">Categoria</dt>
          <dd class="flex-1 text-sm text-textPrimary">Registro de Atividade · Jornada</dd>
        </div>
        <div class="flex gap-3 px-3 py-2">
          <dt class="w-28 text-xs text-neutral50">Tipo</dt>
          <dd class="flex-1 text-sm text-textPrimary" data-testid="send-record-review-type">
            {{ activityTypeLabel }}
          </dd>
        </div>
        <div v-if="description" class="flex gap-3 px-3 py-2">
          <dt class="w-28 text-xs text-neutral50">Descrição</dt>
          <dd class="flex-1 text-sm text-textPrimary">{{ description }}</dd>
        </div>
        <div class="flex gap-3 px-3 py-2">
          <dt class="w-28 text-xs text-neutral50">Médico</dt>
          <dd class="flex-1 text-sm text-textPrimary">{{ doctorLine }}</dd>
        </div>
        <div class="flex gap-3 px-3 py-2">
          <dt class="w-28 text-xs text-neutral50">Vai para</dt>
          <dd class="flex-1 text-sm text-textPrimary">
            Fila de auditoria — filtro Registro de Atividade
          </dd>
        </div>
      </dl>

      <!--
        M6 — o "cara-crachá" também na revisão: o que foi DECLARADO aparece grande,
        ao lado do previsto e do anexo, para a última conferência antes do envio.
      -->
      <p class="text-sm font-bold text-textPrimary">Plantões ({{ lines.length }})</p>
      <div
        v-for="line in lines"
        :key="line.doctorStationId"
        class="rounded-[10px] border border-lightGray30 px-3 py-2.5"
        :data-testid="`send-record-review-line-${line.doctorStationId}`"
      >
        <div class="flex flex-wrap items-center justify-between gap-2">
          <!--
            O texto do cartão continua contendo o resumo de uma linha (data · perna ·
            horários) — é ele que a fila, o e-mail e os specs leem como identidade da linha.
          -->
          <span class="text-[13px] font-bold text-textPrimary">{{ describe(line) }}</span>
          <span
            class="inline-flex flex-none items-center rounded-full bg-primary5 px-2 py-[3px] text-[11.5px] font-bold text-primary40"
          >
            {{ COVERED_LEGS_LABELS[line.coveredLegs] }}
          </span>
        </div>

        <div class="mt-2 grid grid-cols-2 gap-3">
          <div v-for="leg in reviewLegsOf(line)" :key="leg.key">
            <p class="text-[12.5px] font-bold text-neutral50">{{ leg.label }}</p>
            <p class="text-[22px] font-bold tabular-nums text-textPrimary">{{ leg.declared }}</p>
            <p class="text-[11.5px] text-neutral50">{{ leg.expected }}</p>
            <p v-if="leg.diff" class="text-[11.5px]" :class="leg.diffClass">{{ leg.diff }}</p>
          </div>
        </div>
      </div>

      <!-- Avisos nunca bloqueiam: mesmo espírito do `StepWarnings.vue` do wizard de folha. -->
      <ul
        v-if="toleranceTexts.length"
        class="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-900"
        data-testid="send-record-review-warnings"
      >
        <li v-for="text in toleranceTexts" :key="text">{{ text }}</li>
      </ul>

      <!--
        Balão ㉓: sem a declaração marcada o botão fica desabilitado. É a ÚNICA trava dura do
        fluxo inteiro — tolerância, "Registro OK" e pós-plantão fechado são avisos.
      -->
      <label class="flex items-start gap-2 text-sm text-textPrimary cursor-pointer">
        <input
          type="checkbox"
          class="mt-1"
          :checked="truthDeclaration"
          data-testid="send-record-truth-declaration"
          @change="emit('update:truthDeclaration', ($event.target as HTMLInputElement).checked)"
        />
        <span>
          Declaro que as informações e os anexos enviados são verdadeiros e correspondem ao
          atendimento realizado nos plantões acima.
          <span class="block text-xs text-neutral50">
            Sua declaração fica registrada com seu nome, data e hora.
          </span>
        </span>
      </label>

      <div
        v-if="errorMessage"
        class="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        data-testid="send-record-error"
      >
        <p>{{ errorMessage }}</p>
        <button
          v-if="errorCode === ACTIVITY_ERROR_WITH_QUEUE_LINK"
          type="button"
          class="mt-1 text-xs underline font-bold"
          data-testid="send-record-open-queue-link"
          @click="emit('open-queue')"
        >
          Ver na fila
        </button>
      </div>
    </div>

    <div v-if="isDesktop" class="flex flex-col gap-2">
      <AttachmentPanel
        layout="side"
        :files="files"
        :active-index="activeIndex"
        @update:active-index="emit('update:activeIndex', $event)"
      />
      <div class="rounded-[10px] border border-lightGray30 px-3 py-2 text-xs text-neutral50">
        <p class="font-bold text-textPrimary">
          Anexos
          <span v-if="files.length" class="font-normal">
            · {{ files.length }} {{ files.length === 1 ? 'arquivo' : 'arquivos' }} ·
            {{ formatFileSize(totalBytes) }}
          </span>
        </p>
        <p v-for="file in files" :key="file.name" class="mt-0.5 truncate">
          {{ file.name }} · {{ formatFileSize(file.size) }}
        </p>
        <p v-if="!files.length">Nenhum anexo</p>
        <p v-else-if="lines.length > 1" class="mt-1">
          compartilhados pelos {{ lines.length }} registros
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  ACTIVITY_TYPE_LABELS,
  COVERED_LEGS_LABELS,
  CoveredLegs
} from '@/constants/ds-attendance.constants'
import type { ActivityType } from '@/constants/ds-attendance.constants'
import { DESKTOP_MEDIA_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import AttachmentPanel from './AttachmentPanel.vue'
import {
  ACTIVITY_ERROR_WITH_QUEUE_LINK,
  buildToleranceWarnings,
  formatFileSize,
  isoToTimeInput,
  minutesBetween,
  type NormalizedPrefillItem,
  type SendRecordLine
} from './types'

/** Passo 5 de M2 — resumo, declaração de veracidade e envio. */

const props = defineProps<{
  activityType: ActivityType | null
  description: string
  files: File[]
  lines: SendRecordLine[]
  prefill: NormalizedPrefillItem[]
  toleranceTexts: string[]
  doctorLine: string
  truthDeclaration: boolean
  errorCode: string | null
  errorMessage: string | null
  /** Anexo em exibição — compartilhado com as etapas 3 e 4 pelo modal. */
  activeIndex?: number | null
}>()

const emit = defineEmits<{
  'update:truthDeclaration': [value: boolean]
  'update:activeIndex': [value: number]
  'open-queue': []
}>()

/** Só o painel do layout atual é montado — ver o comentário do `v-if` no template. */
const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY)

const activityTypeLabel = computed(() =>
  props.activityType ? ACTIVITY_TYPE_LABELS[props.activityType] : '—'
)

const totalBytes = computed(() => props.files.reduce((sum, file) => sum + file.size, 0))

const prefillById = computed(
  () => new Map(props.prefill.map((item) => [item.doctorStationId, item]))
)

function covers(line: SendRecordLine, leg: CoveredLegs): boolean {
  return line.coveredLegs === leg || line.coveredLegs === CoveredLegs.BOTH
}

/**
 * As duas pernas lado a lado: o declarado em destaque, o previsto embaixo e a
 * diferença em minutos quando há uma — verde dentro da tolerância, amarelo fora
 * (a mesma régua do passo 4, via `buildToleranceWarnings`).
 */
function reviewLegsOf(line: SendRecordLine) {
  const item = prefillById.value.get(line.doctorStationId)
  const warnings = buildToleranceWarnings({
    line,
    expectedStart: item?.expectedStart ?? null,
    expectedEnd: item?.expectedEnd ?? null,
    checkinLateToleranceMinutes: item?.checkinLateToleranceMinutes ?? null,
    checkoutEarlyToleranceMinutes: item?.checkoutEarlyToleranceMinutes ?? null
  })

  return [
    {
      key: 'checkin',
      label: 'Check-in',
      ...legFacts({
        covered: covers(line, CoveredLegs.CHECKIN),
        declared: line.declaredCheckin,
        expected: item?.expectedStart ?? null,
        minutes: minutesBetween(item?.expectedStart ?? null, line.declaredCheckin),
        offset: line.timezone,
        outOfTolerance: warnings.some((warning) => warning.leg === 'CHECKIN')
      })
    },
    {
      key: 'checkout',
      label: 'Check-out',
      ...legFacts({
        covered: covers(line, CoveredLegs.CHECKOUT),
        declared: line.declaredCheckout,
        expected: item?.expectedEnd ?? null,
        minutes: minutesBetween(line.declaredCheckout, item?.expectedEnd ?? null),
        offset: line.timezone,
        outOfTolerance: warnings.some((warning) => warning.leg === 'CHECKOUT')
      })
    }
  ]
}

function legFacts(params: {
  covered: boolean
  declared: string | null
  expected: string | null
  minutes: number | null
  offset: string
  outOfTolerance: boolean
}) {
  if (!params.covered) {
    return { declared: '—', expected: 'fica como o sistema tem', diff: '', diffClass: '' }
  }

  const expected = isoToTimeInput(params.expected, params.offset)
  const difference = params.minutes ?? 0
  return {
    declared: isoToTimeInput(params.declared, params.offset) || '—',
    expected: expected ? `previsto ${expected}` : 'sem previsto',
    diff: difference
      ? `${Math.abs(difference)} min ${params.outOfTolerance ? 'fora da tolerância' : 'dentro da tolerância'}`
      : '',
    diffClass: params.outOfTolerance ? 'text-[#8A6A00]' : 'text-sucess'
  }
}

function describe(line: SendRecordLine): string {
  const item = prefillById.value.get(line.doctorStationId)
  const date = formatDate(item?.expectedStart)
  const legs = COVERED_LEGS_LABELS[line.coveredLegs]
  const times = [
    isoToTimeInput(line.declaredCheckin, line.timezone) || null,
    isoToTimeInput(line.declaredCheckout, line.timezone) || null
  ]
    .filter(Boolean)
    .join('–')
  return [date, legs, times].filter(Boolean).join(' · ')
}

function formatDate(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('pt-BR')
}
</script>
